import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, UserPlus, ArrowLeft, CheckCircle2, ShieldCheck, KeyRound } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: "technician", label: "Técnico ADAS / ADAS Technician" },
  { value: "sales", label: "Vendedor / Sales Rep" },
  { value: "finance", label: "Financeiro / Finance" },
  { value: "reception", label: "Recepção / Reception" },
  { value: "manager", label: "Gerente / Manager" },
];

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    invite_code: "", email: "", password: "", confirm_password: "",
    name: "", phone: "", job_title: "", department: "",
    role: "technician",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.password.length < 6) return setErr("Password must be at least 6 characters.");
    if (form.password !== form.confirm_password) return setErr("Passwords do not match.");
    if (!form.invite_code.trim()) return setErr("Invite code is required.");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/auth/register`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        job_title: form.job_title.trim() || null,
        department: form.department.trim() || null,
        role: form.role,
        invite_code: form.invite_code.trim().toUpperCase(),
      });
      setSuccess(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center bg-[#0B1120] border border-emerald-500/30 rounded-2xl p-10">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-display text-2xl text-emerald-300 mb-2" data-testid="register-success">
            Cadastro enviado!
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed mb-6">
            {success.message || "Your administrator will review and approve your account. You will receive an email once approved."}
          </p>
          <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-[11px] text-slate-500 mb-6">
            <ShieldCheck className="w-3.5 h-3.5 inline-block mr-1 text-emerald-400" />
            Sua conta está com status <b className="text-amber-300">pending_approval</b> até que o administrador libere as permissões.
          </div>
          <Button
            onClick={() => navigate("/login")}
            className="w-full bg-blue-600 hover:bg-blue-500"
            data-testid="register-back-login"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-md w-full">
        <div className="mb-6 flex items-center justify-between">
          <BrandLogo size="md" />
          <a href="/login" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Login
          </a>
        </div>

        <div className="bg-[#0B1120] border border-slate-800 rounded-2xl p-6 sm:p-8">
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.24em] text-blue-400 font-bold mb-1.5">
              CRIAR CONTA
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="register-title">
              Cadastro de funcionário
            </h1>
            <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
              Preencha seus dados. O administrador revisa e libera sua conta.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="register-form">
            {/* Invite code — most prominent */}
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
              <Label htmlFor="invite" className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1.5">
                <KeyRound className="w-3 h-3" /> Código de convite (obrigatório)
              </Label>
              <Input
                id="invite"
                data-testid="register-invite-code"
                value={form.invite_code}
                onChange={(e) => set("invite_code", e.target.value.toUpperCase())}
                placeholder="TRUELINE-XXXX-XXXX"
                required
                className="mt-1.5 bg-slate-950 border-slate-800 font-mono text-sm uppercase focus:border-amber-400"
              />
              <p className="text-[10px] text-slate-500 mt-1">Peça este código ao administrador (Vitor).</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Nome completo</Label>
                <Input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Telefone</Label>
                <Input data-testid="register-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" placeholder="+1 555-000-0000" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-400">E-mail</Label>
              <Input type="email" data-testid="register-email" required value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="bg-slate-950 border-slate-800 mt-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Cargo</Label>
                <Input data-testid="register-job-title" value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" placeholder="ADAS Technician II" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Departamento</Label>
                <Input data-testid="register-department" value={form.department} onChange={(e) => set("department", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" placeholder="Operations" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-400">Função no sistema</Label>
              <select
                data-testid="register-role"
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm mt-1 focus:border-blue-500 focus:outline-none"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1 italic">O admin pode ajustar essa função na hora de aprovar.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Senha</Label>
                <Input type="password" data-testid="register-password" required minLength={6}
                  value={form.password} onChange={(e) => set("password", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Confirmar senha</Label>
                <Input type="password" data-testid="register-confirm-password" required minLength={6}
                  value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)}
                  className="bg-slate-950 border-slate-800 mt-1" />
              </div>
            </div>

            {err && (
              <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs p-2.5" data-testid="register-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-blue-600 hover:bg-blue-500 mt-2 h-11 font-medium"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><UserPlus className="w-4 h-4 mr-2" /> Enviar cadastro</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
